import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Blog } from '@/lib/supabase';
import { formatDate } from '@/lib/utils';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { 
  FileText, 
  Plus, 
  Search, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  Eye,
  CheckCircle,
  XCircle,
  ExternalLink
} from 'lucide-react';

const Blogs = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingBlogId, setDeletingBlogId] = useState<string | null>(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setBlogs(data as Blog[]);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      toast({
        title: "Error",
        description: "Failed to fetch blog posts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePublishStatus = async (blog: Blog) => {
    try {
      const newStatus = !blog.published;
      const publishedAt = newStatus && !blog.published_at ? new Date().toISOString() : blog.published_at;
      
      const { error } = await supabase
        .from('blogs')
        .update({ 
          published: newStatus,
          published_at: publishedAt,
          updated_at: new Date().toISOString()
        })
        .eq('id', blog.id);
        
      if (error) throw error;
      
      // Update local state
      setBlogs(blogs.map(b => 
        b.id === blog.id 
          ? { ...b, published: newStatus, published_at: publishedAt, updated_at: new Date().toISOString() } 
          : b
      ));
      
      toast({
        title: "Success",
        description: `Blog post ${newStatus ? 'published' : 'unpublished'} successfully.`,
      });
    } catch (err) {
      console.error('Error toggling publish status:', err);
      toast({
        title: "Error",
        description: "Failed to update blog status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBlog = async () => {
    if (!deletingBlogId) return;
    
    try {
      // First, delete related blog tags
      const { error: tagError } = await supabase
        .from('blog_tags')
        .delete()
        .eq('blog_id', deletingBlogId);
        
      if (tagError) throw tagError;
      
      // Then delete the blog
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', deletingBlogId);
        
      if (error) throw error;
      
      // Update local state
      setBlogs(blogs.filter(blog => blog.id !== deletingBlogId));
      
      toast({
        title: "Success",
        description: "Blog post deleted successfully.",
      });
    } catch (err) {
      console.error('Error deleting blog:', err);
      toast({
        title: "Error",
        description: "Failed to delete blog post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeletingBlogId(null);
    }
  };

  // Filter blogs based on search query
  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    blog.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout 
      title="Blog Posts" 
      subtitle="Manage your blog content"
    >
      <div className="flex justify-between items-center mb-6">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
          />
        </div>
        
        <Button asChild className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600">
          <Link to="/admin/blogs/new">
            <Plus className="mr-2 h-4 w-4" />
            New Blog Post
          </Link>
        </Button>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full"></div>
        </div>
      ) : filteredBlogs.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-lg">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No blog posts found</h3>
          <p className="text-gray-400 mb-6">
            {searchQuery ? 'No posts match your search query.' : 'Get started by creating your first blog post.'}
          </p>
          {searchQuery ? (
            <Button variant="outline" onClick={() => setSearchQuery('')}>
              Clear Search
            </Button>
          ) : (
            <Button asChild className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600">
              <Link to="/admin/blogs/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Blog Post
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700 hover:bg-gray-800">
                <TableHead className="text-gray-300">Title</TableHead>
                <TableHead className="text-gray-300">Status</TableHead>
                <TableHead className="text-gray-300 hidden md:table-cell">Date</TableHead>
                <TableHead className="text-gray-300 hidden lg:table-cell">Summary</TableHead>
                <TableHead className="text-gray-300 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBlogs.map((blog) => (
                <TableRow key={blog.id} className="border-gray-700 hover:bg-gray-700/50">
                  <TableCell className="font-medium text-white">
                    {blog.title}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={blog.published ? "default" : "secondary"}
                      className={`${blog.published ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'}`}
                    >
                      {blog.published ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-300 hidden md:table-cell">
                    {blog.published 
                      ? `Published ${formatDate(blog.published_at)}` 
                      : `Created ${formatDate(blog.created_at)}`}
                  </TableCell>
                  <TableCell className="text-gray-300 hidden lg:table-cell max-w-xs truncate">
                    {blog.summary}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700 text-gray-200">
                        <DropdownMenuItem 
                          onClick={() => togglePublishStatus(blog)}
                          className="flex items-center cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                        >
                          {blog.published ? (
                            <>
                              <XCircle className="mr-2 h-4 w-4 text-amber-400" />
                              <span>Unpublish</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-2 h-4 w-4 text-green-400" />
                              <span>Publish</span>
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link 
                            to={`/admin/blogs/${blog.id}`}
                            className="flex items-center cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                          >
                            <Pencil className="mr-2 h-4 w-4 text-blue-400" />
                            <span>Edit</span>
                          </Link>
                        </DropdownMenuItem>
                        {blog.published && (
                          <DropdownMenuItem asChild>
                            <a 
                              href={`/blog/${blog.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center cursor-pointer hover:bg-gray-700 focus:bg-gray-700"
                            >
                              <Eye className="mr-2 h-4 w-4 text-teal-400" />
                              <span>View</span>
                              <ExternalLink className="ml-auto h-3 w-3 text-gray-500" />
                            </a>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => setDeletingBlogId(blog.id)}
                          className="flex items-center cursor-pointer hover:bg-gray-700 focus:bg-gray-700 text-red-400"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      <AlertDialog open={!!deletingBlogId} onOpenChange={(open) => !open && setDeletingBlogId(null)}>
        <AlertDialogContent className="bg-gray-800 border-gray-700 text-gray-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this blog post?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action cannot be undone. This will permanently delete the blog post
              and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBlog}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default Blogs; 